import type { BlogPost } from "../blogTypes";

export const howMuchDepositLandlordAskNetherlands: BlogPost = {
    slug: "how-much-deposit-landlord-ask-netherlands",
    title: "How Much Deposit Can a Dutch Landlord Ask? Know Your Rights",
    metaTitle: "Rental Deposit Netherlands | How Much Can Landlords Ask?",
    metaDescription: "How much deposit can a Dutch landlord ask? The statutory two-month basic-rent limit, permitted deductions and the 14/30-day return rules, explained.",
    excerpt: "Deposit disputes are one of the most common issues for renters in the Netherlands. Here's the statutory deposit limit and the 14/30-day return rules, fact-checked against official Dutch sources.",
    date: "2026-02-10",
    updated: "2026-08-23",
    readTime: "3 min read",
    category: "Tenant Rights",
    keywords: ["rental deposit Netherlands", "borg huurwoning", "landlord deposit rules Dutch", "getting deposit back Netherlands"],
    content: `
## Is There a Legal Maximum?

Yes. For a rental contract entered into on or after 1 July 2023, the deposit may not exceed **two months of basic rent (kale huur)**. For contracts entered into before 1 July 2023, [government guidance](https://www.rijksoverheid.nl/vraag-en-antwoord/woning-huren/woning-huren-waarborgsom) says a deposit of up to three months' basic rent may be permitted under case law.

The limit is based on basic rent, not the total monthly payment including service costs, utilities or furniture charges.

In practice, many landlords ask for one or two months' basic rent as a deposit (borg or waarborgsom), but for a post-1 July 2023 contract, anything above two months' basic rent exceeds the statutory limit.

## What the Deposit Can Cover

The statutory categories for deductions are:

- damage for which the tenant is liable;
- unpaid rent;
- unpaid service costs;
- unpaid energy performance fees.

The landlord must not deduct ordinary wear and tear, the landlord's own maintenance obligations, or unrelated fees. See the [government guidance on extra rental costs and deposits](https://www.rijksoverheid.nl/vraag-en-antwoord/woning-huren/welke-extra-kosten-mag-verhuurder-bij-een-huurcontract-vragen) for the full list of what can and cannot be charged.

## When Must the Deposit Be Returned?

If there are no permitted deductions, the landlord must return the deposit **within 14 days** after the tenancy ends. If permitted costs are deducted, the landlord must send a written, itemised explanation and return the balance **within 30 days**.

A contract cannot replace these statutory periods with a general one-to-three-month return window for a tenancy that started on or after 1 July 2023.

### If Your Landlord Won't Return the Deposit

1. Send a clear written request by email or letter and keep proof of delivery or confirmed receipt. Registered post can provide useful evidence, but it is not the only valid way to make the request.
2. Set a reasonable deadline, for example 14 days.
3. If ignored, send a second letter mentioning legal action.
4. File a claim with the **kantonrechter** (sub-district court) if it remains unresolved; get advice on current court fees and procedure before filing.

Note: deposit disputes generally fall outside the Huurcommissie's normal remit — the statutory limit and the kantonrechter are the relevant routes, not a Huurcommissie "reasonableness above three months" standard.

## Tips to Protect Your Deposit

- Always get a receipt for your deposit payment, and make sure the payment is traceable.
- Take dated photos of everything at move-in and move-out.
- Insist on a signed move-in report (opnamerapport). For a contract entered into on or after 1 August 2003, if there is no move-in description, the tenant is generally presumed to have received the property in the condition in which it is returned, unless the landlord proves otherwise. For a contract entered into before 1 August 2003, the older default presumes that the property was received in good condition unless the tenant proves otherwise. A detailed report still helps prevent disputes.
- Clean thoroughly before moving out and fix minor damage you're responsible for.
- Check that your contract states the 14-day/30-day return rules rather than a vague return window.

## Analyze Your Deposit Terms

Your contract's deposit clause matters. [Upload it to RentSense NL](/app) for a one-time €7.99 analysis, developed with reference to current official Dutch government and Huurcommissie sources, and see whether the deposit amount and return conditions match Dutch law.

## Sources checked

- [Government: deposits and the two-month basic-rent limit](https://www.rijksoverheid.nl/vraag-en-antwoord/woning-huren/woning-huren-waarborgsom)
- [Government: extra rental costs and deposit deductions](https://www.rijksoverheid.nl/vraag-en-antwoord/woning-huren/welke-extra-kosten-mag-verhuurder-bij-een-huurcontract-vragen)
- [Huurcommissie: procedure costs](https://www.huurcommissie.nl/onderwerpen/procedure/procedure-starten/kosten)
- [Rechtspraak: kantonrechter fees](https://www.rechtspraak.nl/naar-de-rechter/kosten-rechtszaak/griffierecht/griffierecht-kanton)
    `,
};
